import { deepEquals } from './utils/common-utils';
import { oldToNewComponentValueMap, v1ComponentsSerializationMap } from './v1-components-map';
import { legacyComponentsMapCreator } from './legacy-components-map';
import type { DomParser, DomNode } from './types';

interface VersionedComponent {
	type: string;
	value: any;
}

export const consolidateTextComponents = (components: VersionedComponent[]): VersionedComponent[] =>
	components.reduce<VersionedComponent[]>((combined, component) => {
		const previous = combined[combined.length - 1];
		if (component.type === 'text' && previous && previous.type === 'text') {
			previous.value.html += component.value.html;
		} else {
			combined.push(component);
		}
		return combined;
	}, []);

export const isValidV1HtmlContent = ($: DomParser, html: string): boolean => {
	try {
		const parsedComponents = deserializeAsV1($, html);
		const reversedHtml = parsedComponents
			.map((component) => {
				const innerValue = (v1ComponentsSerializationMap($) as Record<string, { valueToHtml: (v: unknown) => string }>)[component.type].valueToHtml(component.value);
				return `<div data-component-type="${component.type}">${innerValue}</div>`;
			})
			.join('');

		const doubleParsedComponents = deserializeAsV1($, reversedHtml);

		const $children = $('<div>').html(html).children();
		const componentCount = $children.filter('div[data-component-type]').length;
		const childrenCount = $children.length;

		const childrenCountMatch = childrenCount === parsedComponents.length;
		const componentCountMatch = componentCount === parsedComponents.length;
		const hasComponents = componentCount > 0;
		const componentContentMatch = deepEquals(parsedComponents, doubleParsedComponents);

		return hasComponents && childrenCountMatch && componentCountMatch && componentContentMatch;
	} catch {
		return false;
	}
};

export const deserializeAsV1 = ($: DomParser, oldVersionedHtml: string): VersionedComponent[] =>
	$(oldVersionedHtml)
		.filter('div[data-component-type]')
		.toArray()
		.map((node: unknown) => {
			const $node = $(node);
			const type: string = $node.attr('data-component-type');
			const value = (v1ComponentsSerializationMap($) as Record<string, { htmlNodeToValue: (n: DomNode) => unknown }>)[type].htmlNodeToValue($node.contents());
			return { type, value };
		});

export const deserializeAsLegacy = ($: DomParser, oldVersionedHtml: string): VersionedComponent[] => {
	const $node = $('<div class="dummy">').html(oldVersionedHtml);

	$node.find('iframe')
		.toArray()
		.reverse()
		.forEach((node: unknown) => {
			const $iframe = $(node);
			const $parent = $iframe.parent();
			if (!$parent.is('.dummy')) {
				$iframe.remove().insertAfter($parent);
			}
		});

	$node.find('.step-by-step-widget').each(function (this: unknown) {
		const $sbs = $(this);
		while (!$sbs.parent().is('.dummy')) {
			$sbs.parent().replaceWith($sbs);
		}
	});

	const components: VersionedComponent[] = $node
		.children()
		.toArray()
		.map((child: unknown) => {
			const $child = $(child);
			const innerVal: string = $('<div>').append($child.clone()).html();
			const result: VersionedComponent = {
				type: 'text',
				value: { html: innerVal },
			};
			let found = false;

			const legacyComponentsMap = legacyComponentsMapCreator($) as Record<string, (n: DomNode) => unknown>;
			Object.keys(legacyComponentsMap).forEach((type) => {
				const fn = legacyComponentsMap[type];
				const val = fn($child);
				if (found) {
					return;
				}
				if (val) {
					found = true;
					result.type = type;
					result.value = val;
				}
			});

			if (result.type === 'text') {
				const oldNoteContent = $node.find('.kb-note').html();
				$node.find('.kb-note').removeClass('kb-note').addClass('note').html('<i>' + oldNoteContent + '</i>');
				result.value.html = $('<div>').append($child.clone()).html();
			}

			return result;
		});

	return consolidateTextComponents(components);
};

export const legacyDeserializerHandle = ($: DomParser) => (oldVersionedHtml: string, locale: string) => {
	const oldVersionedComponents = isValidV1HtmlContent($, oldVersionedHtml)
		? deserializeAsV1($, oldVersionedHtml)
		: deserializeAsLegacy($, oldVersionedHtml);

	const mapper = oldToNewComponentValueMap(locale, $);
	return oldVersionedComponents.map((c) => (mapper as Record<string, (v: unknown) => VersionedComponent>)[c.type](c.value));
};
